<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core"
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { CalendarState } from "primevue/calendar"
import moment from "moment"

const emit = defineEmits(["click:optimal"])

const props = withDefaults(defineProps<Props>(), {
    freqs: () => ["raw", "hourly"],
    loading: false,
    optimalButton: false,
})
type Props = {
    /**
     * Liste des graphs disponibles
     */
    optionGraphs: string[]

    /**
     * Liste des fréquences disponibles
     */
    freqs: string[]
    /**
     * Etat de chargement. Affiche un loader dans le select des graphiques.
     */
    loading?: boolean
    /**
     * Affiche le bouton "Voir graphiques avec des données disponibles"
     */
    optimalButton?: boolean
    /**
     * permet de cacher le select des graphiques
     */
    hideSelectGraphs?: boolean
}

const graphStore = useGraphStore()
const { t, te } = useI18n({
    messages: {
        fr: {
            raw: "Donnée brute",
            hourly: "Horaire",
            daily: "Journalière",
            weekly: "Hebdomadaire",
            monthly: "Mensuelle",
            freq_placeholder: "Fréquence selectionnée",
            date_placeholder: "Sélectionne une date",
            graph_placeholder: "Sélectionne un graphique",
            days: "jour | jours",
            tooltip_optimal:
                "Afficher seulement les graphiques avec des données disponibles pour votre bâtiment",
            optimal: "Voir graphiques avec des données disponibles",
            one_week: "1 semaine",
            one_month: "1 mois",
            three_month: "3 mois",
            six_month: "6 mois",
            one_year: "1 an",
            graph_max_selected: "{0} graphiques sélectionnés",
            monthly_warning:
                "Les données mensuelles ne sont accessibles que pour les mois précédents",
        },
    },
})

/**
 * Emit lors des updates des variables suivantes
 */
const [modelPeriod] = defineModel<[Date, Date]>("period", {
    required: true,
    default: [new Date(), new Date()],
})
const [modelGraphs] = defineModel<string[]>("graphs", {
    required: true,
    default: [],
})
const [modelFreq] = defineModel<string>("freq", {
    required: true,
})

// calendar reference (CalendarState interface for typescript)
const calendar = ref<CalendarState>({} as CalendarState)

// ================================
// WATCHERS
// ================================

/**
 * Watch optionGraphs pour ajuster le model : filtre les graphiques qui ne sont plus dans les options.
 * Quand optionGraphs est vide, vide le model. Quand le model est vide et optionGraphs a des valeurs,
 * on ne touche pas au model (la page peut l'initialiser depuis le localStorage ensuite).
 */
watch(
    () => props.optionGraphs,
    (newOptions) => {
        if (!newOptions || newOptions.length === 0) {
            modelGraphs.value = []
            return
        }
        if (!modelGraphs.value?.length) {
            return
        }
        modelGraphs.value = modelGraphs.value.filter((graphId) =>
            newOptions.includes(graphId)
        )
    },
    { immediate: true, deep: true }
)

/**
 * Renvois la différence en jours entres deux dates
 * @param { Date, Date}
 * @returns {Number}
 */
const differenceInDays = computed(() => {
    if (
        modelPeriod.value.length === 2 &&
        modelPeriod.value[1] !== null &&
        modelPeriod.value[0] !== null
    ) {
        const [startDate, endDate] = modelPeriod.value
        const time1 = startDate.getTime()
        const time2 = endDate.getTime()
        const diffInMilliseconds = Math.abs(time2 - time1)
        return Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24))
    } else return 0
})
/**
 * Renvois la liste des options de fréquence
 */
const getFreqOptions = computed(() => {
    return props.freqs.map((freq) => {
        return {
            value: freq,
            name: t(freq),
        }
    })
})

/**
 * Renvois la liste des options de graphique
 */
const getGraphOptionList = computed(() => {
    return (
        props.optionGraphs
            .map((id) => {
                const graph = graphStore.getGraph(id)
                if (!graph) return null
                return {
                    value: id,
                    name: graph?.name,
                }
            })
            .filter((el) => el !== null)
            // regroupe par catégorie, si pas de catégorie alors met dans la catégorie ""
            .reduce(
                (
                    acc: { label: string; items: any[]; value: string }[],
                    curr
                ) => {
                    const graph = graphStore.getGraph(curr.value)
                    const category = graph?.category || ""

                    if (
                        !acc.find(
                            (el: { value: string }) => el.value === category
                        )
                    ) {
                        acc.push({
                            value: category,
                            label: te(`category.${category}`)
                                ? t(`category.${category}`)
                                : category,
                            items: [curr],
                        })
                    } else {
                        const categoryIndex = acc.findIndex(
                            (el: { value: string }) => el.value === category
                        )
                        // met à jour la liste des items
                        acc[categoryIndex].items.push(curr)
                    }
                    return acc
                },
                []
            )
    )
})

const getPeriodOptions = computed(() => {
    let options = [
        { label: t("one_week"), value: "1w" },
        { label: t("one_month"), value: "1m" },
        { label: t("six_month"), value: "6m" },
        { label: t("one_year"), value: "1y" },
    ]

    if (modelFreq.value === "monthly") {
        options = options.filter((option) => option.value !== "1w")
        // Ajoute le filtre 3 mois
        options.splice(1, 0, { label: t("three_month"), value: "3m" })
    }
    return options
})

const getMaxDateByFreq = computed(() => {
    if (modelFreq.value === "monthly") {
        if (moment().date() !== moment().endOf("month").date()) {
            return moment().subtract(1, "month").endOf("month").toDate()
        } else {
            return moment().endOf("month").toDate()
        }
    } else {
        return moment().toDate()
    }
})

/**
 * quand on clique sur un bouton de date
 * @param period - période sélectionnée
 */
function onClickDatePickerButton(period: string) {
    let date = moment()
    let dateTo = moment()
    switch (period) {
        case "1w":
            date = moment().subtract(7, "days")
            break

        case "1y":
            if (modelFreq.value === "monthly") {
                // Si la date du jour n'est pas la fin du mois, on affiche le mois M-1
                const lastDayOfMonth = moment().endOf("month").date()
                if (moment().date() !== lastDayOfMonth) {
                    // Aller au premier jour du mois précédent
                    date = moment().subtract(1, "year").startOf("month")
                    dateTo = moment().subtract(1, "month").endOf("month")
                } else {
                    date = moment().startOf("month")
                    dateTo = moment().startOf("month")
                }
            } else {
                date = moment().subtract(1, "year")
            }
            break
        // Gestion générique pour les périodes de type "Xm" (mois)
        default:
            if (/^\d+m$/.test(period)) {
                const months = parseInt(period.replace("m", ""), 10)
                if (modelFreq.value === "monthly") {
                    const lastDayOfMonth = moment().endOf("month").date()
                    if (moment().date() !== lastDayOfMonth) {
                        // Aller au premier jour du mois X mois avant
                        date = moment()
                            .subtract(months, "month")
                            .startOf("month")
                        dateTo = moment().subtract(1, "month").endOf("month")
                    } else {
                        date = moment().startOf("month")
                        dateTo = moment().startOf("month")
                    }
                } else {
                    date = moment().subtract(months, "month")
                }
                break
            }
    }
    modelPeriod.value = [date.toDate(), dateTo.toDate()]
    // fermer le datepicker
    if (!calendar.value) return

    calendar.value.overlayVisible = false
}
/**
 * Mise à jour de la fréquence sélectionnée. Dans le cas où la nouvelle fréquence est mensuelle,
 * on réinitialise la période sélectionnée si la date de fin est dans le mois en cours non terminée
 * @param freq - fréquence sélectionnée
 */
function onUpdateModelFreq(freq: string) {
    if (freq !== "monthly") return

    nextTick(() => {
        if (modelPeriod.value[1] < getMaxDateByFreq.value) return

        const start = moment(modelPeriod.value[0]).startOf("month")
        const end = moment(modelPeriod.value[1]).startOf("month")
        let count = 0
        for (
            let current = start.clone();
            current.isSameOrBefore(end);
            current.add(1, "month")
        ) {
            count++
        }
        onClickDatePickerButton(`${count === 1 ? "1" : count - 1}m`)
    })
}

/**
 * Quand la période est mise à jour, si la fréquence est mensuelle, on met la date de fin à la fin du mois
 * @param period - période sélectionnée
 */
function onUpdateModelPeriod(period: [Date, Date]) {
    if (modelFreq.value === "monthly" && period[0] && period[1]) {
        // Met la date de fin à la fin du mois
        period[1] = moment(period[1]).endOf("month").toDate()

        modelPeriod.value = period
    }
}

const debounced = useDebounceFn(() => {
    emit("click:optimal")
}, 500)
</script>

<template>
    <div class="data-header box-border flex flex-wrap items-end gap-4">
        <!-- Sélecteur de fréquence -->
        <BaseFloatLabel
            variant="on"
            class="data-header__freq min-w-64 [grid-area:freq]"
        >
            <BaseSelect
                class="w-full"
                id="freq"
                v-model="modelFreq"
                :options="getFreqOptions"
                option-label="name"
                option-value="value"
                :placeholder="t('freq_placeholder')"
                @update:model-value="onUpdateModelFreq"
            />
            <label for="freq">Fréquence</label>
        </BaseFloatLabel>
        <!-- Sélecteur de dates -->
        <BaseFloatLabel
            variant="on"
            class="data-header__date-picker min-w-64 [grid-area:period]"
        >
            <BaseDatePicker
                class="data-header__date-picker-panel w-full"
                id="period"
                ref="calendar"
                show-icon
                selection-mode="range"
                :view="modelFreq === 'monthly' ? 'month' : 'date'"
                date-format="dd/mm/yy"
                :manual-input="false"
                v-model="modelPeriod"
                :max-date="getMaxDateByFreq"
                hide-on-range-selection
                :show-week="modelFreq === 'weekly'"
                panel-class="min-w-[392px]"
                :placeholder="t('date_placeholder')"
                pt:month:class="m-1"
                pt:month-view:class="flex flex-row flex-wrap justify-center"
                pt:year:class="m-1"
                pt:year-view:class="flex flex-row flex-wrap justify-center"
                @update:model-value="onUpdateModelPeriod"
            >
                <template #footer>
                    <div
                        class="data-header__date-picker-button grid grid-cols-[6rem_5rem_5rem_5rem] justify-center gap-1"
                    >
                        <BaseButton
                            v-for="period in getPeriodOptions"
                            :key="period.value"
                            size="small"
                            outlined
                            :label="period.label"
                            severity="secondary"
                            @click="onClickDatePickerButton(period.value)"
                        ></BaseButton>
                    </div>
                    <BaseMessage
                        v-if="modelFreq === 'monthly'"
                        severity="warn"
                        size="small"
                        class="mt-2"
                    >
                        {{ t("monthly_warning") }}
                    </BaseMessage>
                </template>
            </BaseDatePicker>
            <label for="period">Période</label>
        </BaseFloatLabel>
        <!-- Amplitude -->
        <div
            class="data-header__number-of-days px-4 pb-3 pt-4 [grid-area:days]"
        >
            <span class="text-primary whitespace-nowrap text-center"
                >{{ differenceInDays }} {{ t("days", differenceInDays) }}</span
            >
        </div>
        <!-- Sélecteur de graphiques -->
        <BaseFloatLabel
            variant="on"
            v-if="!hideSelectGraphs"
            class="data-header__graph float-right flex-1 [grid-area:graph]"
        >
            <BaseMultiSelect
                class="w-full"
                id="multi_graph"
                v-model="modelGraphs"
                :options="getGraphOptionList"
                option-value="value"
                option-label="name"
                option-group-label="label"
                option-group-children="items"
                :max-selected-labels="1"
                selected-items-label="{0} graphiques sélectionnés"
                display="chip"
                filter
                :loading="loading"
            >
                <template #footer>
                    <div class="p-3" v-if="optimalButton">
                        <BaseButton
                            :label="t('optimal')"
                            severity="secondary"
                            v-tooltip="t('tooltip_optimal')"
                            text
                            size="small"
                            icon="pi pi-chart-bar"
                            @click="debounced"
                        />
                    </div>
                </template>
            </BaseMultiSelect>
            <label for="multi_graph">Graphiques</label>
        </BaseFloatLabel>
    </div>
</template>
